import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Pause, Play } from "lucide-react";
import { SCENES } from "./demoScenes";

const pct = (v) => `${v}%`;
const fade = (at) => ({ initial: { opacity: 0, scale: 0.9 }, animate: { opacity: 1, scale: 1 }, transition: { delay: at, duration: 0.35 } });

const Cursor = ({ path, duration }) => (
  <motion.div className="absolute z-20 pointer-events-none" style={{ left: pct(path[0][0]), top: pct(path[0][1]) }}
    animate={{ left: path.map((p) => pct(p[0])), top: path.map((p) => pct(p[1])) }}
    transition={{ duration, times: path.map((p) => p[2] / duration), ease: "easeInOut" }}>
    <svg width="22" height="22" viewBox="0 0 24 24" className="drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
      <path d="M4 2l15 10.5-6.6 1.2 3.9 7.4-3 1.6-3.9-7.5L4 20z" fill="#0b1e3f" stroke="white" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  </motion.div>
);

const Click = ({ x, y, at }) => (
  <motion.span className="absolute z-10 h-10 w-10 -ml-5 -mt-5 rounded-full border-2 border-sky-400 bg-sky-400/30 pointer-events-none"
    style={{ left: pct(x), top: pct(y) }} initial={{ scale: 0, opacity: 0 }} animate={{ scale: [0, 1.6], opacity: [0.9, 0] }} transition={{ delay: at, duration: 0.6 }} />
);

const Item = ({ it }) => {
  const pos = { left: pct(it.x), top: pct(it.y) };
  if (it.type === "ring") return <motion.div {...fade(it.at)} className="absolute rounded-md ring-[3px] ring-sky-400 shadow-[0_0_0_9999px_rgba(9,13,22,0.35)]" style={{ ...pos, width: pct(it.w), height: pct(it.h) }} />;
  if (it.type === "check") return <motion.span {...fade(it.at)} className="absolute -translate-x-1/2 -translate-y-1/2 grid place-items-center w-[1.5%] aspect-square rounded-[3px] bg-blue-600" style={pos}><Check className="w-full h-full text-white p-[1px]" strokeWidth={4} /></motion.span>;
  if (it.type === "button") return <motion.div {...fade(it.at)} className="absolute grid place-items-center rounded-md bg-blue-600 text-white font-semibold text-[1.1vw] lg:text-sm" style={{ ...pos, width: pct(it.w), height: pct(it.h) }}>{it.text}</motion.div>;
  return (
    <motion.div {...fade(it.at)} className="absolute z-10 rounded-lg bg-[#0b1e3f] text-white text-[10px] sm:text-xs lg:text-sm font-medium px-3 py-2 shadow-xl border border-sky-400/40 whitespace-nowrap" style={pos}>
      <span className="inline-block h-2 w-2 rounded-full bg-emerald-400 mr-2" />{it.text}
    </motion.div>
  );
};

export const Scene = ({ s }) => (
  <motion.div key={s.img} className="absolute inset-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
    <img src={`/landing/${s.img}.jpg`} alt={s.cap} className="absolute inset-0 w-full h-full object-cover" />
    {s.items.map((it, i) => <Item key={i} it={it} />)}
    {s.clicks.map((c, i) => <Click key={i} {...c} />)}
    <Cursor path={s.path} duration={s.duration} />
  </motion.div>
);

export const DemoPlayer = () => {
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);
  const s = SCENES[i];
  useEffect(() => {
    if (!playing) return undefined;
    const t = setTimeout(() => setI((n) => (n + 1) % SCENES.length), s.duration * 1000);
    return () => clearTimeout(t);
  }, [i, playing, s.duration]);

  return (
    <div className="rounded-2xl overflow-hidden border border-slate-800 bg-slate-900 shadow-[0_30px_80px_-20px_rgba(56,189,248,0.25)] max-w-5xl" data-testid="demo-player">
      <div className="relative w-full aspect-video overflow-hidden bg-black">
        <AnimatePresence mode="sync"><Scene key={`${i}`} s={s} /></AnimatePresence>
      </div>
      <div className="px-4 sm:px-6 py-4 border-t border-slate-800">
        <AnimatePresence mode="wait">
          <motion.p key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} className="text-base sm:text-lg font-semibold text-white min-h-[3.5rem] sm:min-h-0" data-testid="demo-caption">
            <span className="text-sky-400 font-mono mr-2">{i + 1}/{SCENES.length}</span>{s.cap}
          </motion.p>
        </AnimatePresence>
        <div className="flex items-center gap-3 mt-3">
          <button type="button" onClick={() => setPlaying((p) => !p)} className="h-8 w-8 grid place-items-center rounded-full bg-slate-800 hover:bg-slate-700 text-white transition-colors" aria-label={playing ? "Pause" : "Lecture"} data-testid="demo-play-toggle">
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </button>
          <div className="flex-1 grid gap-2" style={{ gridTemplateColumns: `repeat(${SCENES.length}, 1fr)` }}>
            {SCENES.map((sc, n) => (
              <button key={sc.img + n} type="button" onClick={() => setI(n)} className="text-left group" data-testid={`demo-step-${n + 1}`}>
                <span className="block h-1 rounded-full bg-slate-700 overflow-hidden">
                  {n < i && <span className="block h-full w-full bg-sky-400" />}
                  {n === i && <motion.span key={`${i}-${playing}`} className="block h-full bg-sky-400" initial={{ width: "0%" }} animate={{ width: playing ? "100%" : "0%" }} transition={{ duration: playing ? sc.duration : 0, ease: "linear" }} />}
                </span>
                <span className={`hidden sm:block text-xs mt-1.5 transition-colors ${n === i ? "text-white font-semibold" : "text-slate-500 group-hover:text-slate-300"}`}>{sc.step}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
